import React from 'react'
import { Chip, Typography } from '@mui/material'
import { supabase } from '../../supabaseClient'

// Shared parent-portal signup status: chip + status mapping + a batch loader.
// Used by the students table and the class roster so both stay consistent.

export type ParentSignup = 'signed_up' | 'invited' | 'not_invited' | 'none'

export function parentSignupOf(status: string | undefined | null): ParentSignup {
  if (!status) return 'none'
  if (status === 'active') return 'signed_up'
  if (status === 'pending_activation') return 'invited'
  return 'not_invited'
}

export function ParentSignupChip({ value }: { value: ParentSignup }) {
  if (value === 'none')      return <Typography variant="body2" color="text.disabled">—</Typography>
  if (value === 'signed_up') return <Chip size="small" color="success" label="Signed up" />
  if (value === 'invited')   return <Chip size="small" color="warning" label="Invited" />
  return <Chip size="small" variant="outlined" label="Not invited" />
}

// student_id → signup state (best across linked parents:
// active > pending_activation > inactive). Reads RLS-visible
// parent_student_link + users (staff can read both).
export async function loadParentSignup(studentIds: string[]): Promise<Record<string, ParentSignup>> {
  if (studentIds.length === 0) return {}
  const { data: links } = await supabase
    .from('parent_student_link').select('student_id, parent_user_id').in('student_id', studentIds)
  const parentIds = Array.from(new Set((links ?? []).map(l => l.parent_user_id)))
  const { data: parents } = parentIds.length > 0
    ? await supabase.from('users').select('user_id, status').in('user_id', parentIds)
    : { data: [] as { user_id: string; status: string }[] }
  const statusById: Record<string, string> = Object.fromEntries((parents ?? []).map(u => [u.user_id, u.status]))

  const rank = (s: string) => (s === 'active' ? 3 : s === 'pending_activation' ? 2 : 1)
  const bestRank: Record<string, number> = {}
  const best: Record<string, ParentSignup> = {}
  for (const l of links ?? []) {
    const st = statusById[l.parent_user_id]
    if (!st) continue
    const r = rank(st)
    if (r > (bestRank[l.student_id] ?? 0)) {
      bestRank[l.student_id] = r
      best[l.student_id] = parentSignupOf(st)
    }
  }
  return best
}
